// Forma para llenar contrato

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  Typography
} from "@mui/material";
import { getServiceTypes } from "./ServiceType";
import { formatMXDateTime } from '../util/DateUtil';
import { validateField, nameValidator, requiredValidator } from "../validators";
import { apiFetchJson } from '../fetchWrapper';
import { getQuatationStatusDisplayName } from "./QuatationStatus";

export default function ContractForm({ contract, onBack, onSave, fromQuotation = false }) {

  const emptyForm = {
    organizationName: '',
    description: '',
    elementsNumber: '',
    serviceType: '',
    notes: '',
    clientId: '',
    quotationId: '',
    startDate: '',
    endDate: ''
  };

  const initForm = contract ? {
    organizationName: contract.organizationName || '',
    description: contract.description || '',
    elementsNumber: contract.elementsNumber || '',
    supervisorsNumber: contract.supervisorsNumber || '',
    serviceType: contract.serviceType || '',
    notes: contract.notes || '',
    clientId: contract.clientId || '',
    quotationId: contract.quotationId || '',
    startDate: contract.startDate || '',
    endDate: contract.endDate || ''
  } : emptyForm;

  const [localContract, setLocalContract] = useState(contract || {});
  const [status, setStatus] = useState(contract?.status);
  const [notes, setNotes] = useState(contract?.notes);
  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState({});
  const [clients, setClients] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const hasErrors = Object.keys(errors).length > 0;
  const isEdit = !!contract;

  const requiredFields = [
    'organizationName',
    'description',
    'elementsNumber',
    'supervisorsNumber',
    'serviceType',
    'clientId',
    'startDate',
    'endDate'
  ];

  const missingRequiredFields = requiredFields.filter((field) => {
    const value = form[field];
    return value === undefined || value === null || value === '' || value === 0;
  });

  const requiredFieldsFilled = missingRequiredFields.length === 0;

  useEffect(() => {
    apiFetchJson("/api/clients").then(setClients);
    apiFetchJson("/api/quotations").then(setQuotations);
  }, [contract]);

  const handleSave = async () => {
    try {
      const isEdit = !!contract && !fromQuotation;
      const url = isEdit
        ? `/api/contracts/${contract.id}`
        : `/api/contracts`;

      const method = isEdit ? "PUT" : "POST";

      const saved = await apiFetchJson(url, {
        method,
        body: JSON.stringify(form)
      });

      setLocalContract(saved);
      setForm({
        organizationName: saved.organizationName,
        description: saved.description,
        elementsNumber: saved.elementsNumber,
        supervisorsNumber: saved.supervisorsNumber,
        serviceType: saved.serviceType,
        notes: saved.notes,
        clientId: saved.clientId,
        quotationId: saved.quotationId,
        startDate: saved.startDate,
        endDate: saved.endDate
      });
      if (onSave) {
        console.log("contract saved, calling onSave callback", saved);
        onSave(saved);
      }

    } catch (err) {
      console.error(err);
      alert("Error al guardar el contrato");
    }
  };

  const handleCancel = () => {
    setForm(initForm);
  }

  useEffect(() => {
    setErrors((prev) => {
      const next = { ...prev };

      const start = form.startDate;
      const end = form.endDate;

      // Solo validar si ambos están presentes
      if (start && end) {
        const startDate = new Date(start);
        const endDate = new Date(end);

        if (startDate > endDate) {
          next.startDate = "La fecha de inicio debe ser menor a la fecha de fin";
          next.endDate = "La fecha de fin debe ser mayor a la fecha de inicio";
        } else {
          delete next.startDate;
          delete next.endDate;
        }
      }

      return next;
    });
  }, [form.startDate, form.endDate]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors(prev => {
      const next = { ...prev };
      let error = null;

      if (name === 'organizationName') {
        error = validateField(value, [
          requiredValidator,
          nameValidator
        ]);
      }

      if (name === 'clientId') {
        error = validateField(value, [
          requiredValidator
        ]);
      }

      if (name === 'elementsNumber' || name === 'supervisorsNumber') {
        error = validateField(value, [
          requiredValidator
        ]);
      }

      if (name === 'description' || name === 'notes') {
        error = validateField(value, [
          requiredValidator,
          nameValidator
        ]);
      }

      if (name === 'serviceType' || name === 'startDate' || name === 'endDate') {
        error = validateField(value, [
          requiredValidator
        ]);
      }

      if (error) {
        next[name] = error;
      } else {
        delete next[name];
      }
      return next;
    });
  };

  const approvedQuotations = quotations.filter(q => q.status === 'APPROVED');

  return (

    <div style={{ maxWidth: "800px", margin: "20px auto" }}>
      {!fromQuotation && (
        <Button
          sx={{
            mb: 2,
            backgroundColor: "#3f74b1",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#364f6b"
            }
          }}
          onClick={onBack}

        >
          Volver
        </Button>
      )}
      {fromQuotation && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" gutterBottom textAlign="center">
            Registro nuevo cliente
          </Typography>
        </Box>
      )}

      <TableContainer style={{ marginBottom: "20px" }}>
        <Table
          sx={{
            '& .MuiTableCell-root': {
              borderBottom: '1px solid #eaeaea',
              py: 1.5,
            }
          }}
        >
          <TableBody>

            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
            </TableRow>

            <TableRow>
              <TableCell><strong>Organización:</strong></TableCell>
              <TableCell>
                <TextField
                  name="organizationName"
                  error={!!errors.organizationName}
                  helperText={errors.organizationName}
                  value={form.organizationName}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 50 } }}
                  fullWidth
                  required
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell><strong>Cliente:</strong></TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  select label="Cliente"
                  error={!!errors.clientId}
                  helperText={errors.clientId}
                  value={form.clientId}
                  name="clientId"
                  onChange={handleChange}
                  required
                >
                  {
                    clients.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.companyName}
                      </MenuItem>
                    ))
                  }
                </TextField>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell><strong>Cotización:</strong></TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  select label="Cotización"
                  name="quotationId"
                  value={form.quotationId}
                  onChange={handleChange}
                >
                  {
                    approvedQuotations.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.id}: {option.organizationName} - {getQuatationStatusDisplayName(option.status)}
                      </MenuItem>
                    ))
                  }
                </TextField>
              </TableCell>
            </TableRow>


            <TableRow>
              <TableCell><strong>Descripción:</strong></TableCell>
              <TableCell>
                <TextField
                  multiline
                  minRows={3}
                  name="description"
                  error={!!errors.description}
                  helperText={errors.description}
                  value={form.description}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 512 } }}
                  fullWidth
                  required
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell ><strong>Número de elementos:</strong></TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  name="elementsNumber"
                  type='number'
                  error={!!errors.elementsNumber}
                  helperText={errors.elementsNumber}
                  value={form.elementsNumber}
                  onChange={(e) => {
                    const raw = e.target.value;

                    // Permitir valores vacíos (importante para Firefox)
                    if (raw === '') {
                      setForm((prev) => ({
                        ...prev,
                        elementsNumber: '',
                      }));
                      return;
                    }

                    let value = Number(raw);

                    if (value < 1) value = 1;
                    if (value > 100) value = 100;

                    setForm((prev) => ({
                      ...prev,
                      elementsNumber: value,
                    }));
                  }}
                >
                </TextField>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell ><strong>Número de supervisores:</strong></TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  name="supervisorsNumber"
                  type='number'
                  error={!!errors.supervisorsNumber}
                  helperText={errors.supervisorsNumber}
                  value={form.supervisorsNumber}
                  onChange={(e) => {
                    const raw = e.target.value;

                    // Permitir campo vacío (importante para Firefox)
                    if (raw === '') {
                      setForm((prev) => ({
                        ...prev,
                        supervisorsNumber: '',
                      }));
                      return;
                    }

                    let value = Number(raw);

                    if (value < 1) value = 1;
                    if (value > 10) value = 10;

                    setForm((prev) => ({
                      ...prev,
                      supervisorsNumber: value,
                    }));
                  }}
                >
                </TextField>
              </TableCell>
            </TableRow>


            <TableRow>
              <TableCell><strong>Tipo de servicio:</strong></TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  select label="Tipo de servicio"
                  name="serviceType"
                  error={!!errors.serviceType}
                  helperText={errors.serviceType}
                  value={form.serviceType}
                  onChange={handleChange}
                  required
                >
                  {
                    getServiceTypes().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  }
                </TextField>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell><strong>Observaciones:</strong></TableCell>
              <TableCell>
                <TextField
                  multiline
                  minRows={3}
                  name="notes"
                  error={!!errors.notes}
                  helperText={errors.notes}
                  value={form.notes}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell><strong>Fecha de empiezo:</strong></TableCell>
              <TableCell>
                <TextField
                  name="startDate"
                  type="date"
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  value={form.startDate}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell><strong>Fecha de fin de contrato:</strong></TableCell>
              <TableCell>
                <TextField
                  name="endDate"
                  type="date"
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  value={form.endDate}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell><strong>Fecha de creación:</strong></TableCell>
              <TableCell>{localContract.createdAt && formatMXDateTime(localContract.createdAt)}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell><strong>Última actualización:</strong></TableCell>
              <TableCell>{localContract.updatedAt && formatMXDateTime(localContract.updatedAt)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>

          <Button onClick={onBack}>
            Volver
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button color="error" onClick={handleCancel}>
              Restablecer
            </Button>

            <Button
              variant="contained"
              disabled={hasErrors || !requiredFieldsFilled}
              onClick={handleSave}
              sx={{
                backgroundColor: "#3f74b1",
                "&:hover": { backgroundColor: "#364f6b" }
              }}
            >
              Guardar
            </Button>
          </Box>
        </Box>
      </TableContainer>
    </div>
  );
}